import { FileText } from 'lucide-react'
import { formatBytes } from '@/utils/formatBytes'
import { useMediaDownload } from './mediaLink'
import { MEDIA_TEXT } from './mediaViewText'

export interface MediaDownloadButtonProps {
  messageId: string
  mediaUrl: string
  filename?: string | null
  byteSize?: number | null
}

/**
 * SA-979 (design note §4.2): nothing is fetched until the click. The click
 * mints a 60-second link and follows it through a temporary <a download>, so
 * the browser streams the file to disk with its own progress, at any size.
 * SA-1094: shared by the attachment and the inline player's fallback.
 */
export function MediaDownloadButton({ messageId, mediaUrl, filename, byteSize }: MediaDownloadButtonProps) {
  const { state, download } = useMediaDownload(messageId, mediaUrl, filename)

  if (state === 'gone') {
    return <p className="text-xs text-muted-foreground italic mb-1">{MEDIA_TEXT.gone}</p>
  }
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={download}
        disabled={state === 'preparing'}
        className="text-xs text-primary flex items-center gap-1 min-w-0 max-w-full text-left disabled:opacity-60"
      >
        <FileText className="w-3 h-3 shrink-0" aria-hidden="true" />
        {/* SA-1110: the name wraps inside the bubble; only it is underlined. */}
        <span className="underline min-w-0 [overflow-wrap:anywhere]">
          {state === 'preparing' ? MEDIA_TEXT.preparingDownload : filename || MEDIA_TEXT.downloadAttachment}
        </span>
        {state !== 'preparing' && byteSize != null && (
          <span className="text-muted-foreground shrink-0"> · {formatBytes(byteSize)}</span>
        )}
      </button>
      {state === 'failed' && (
        <p className="text-xs text-destructive italic mt-1">{MEDIA_TEXT.downloadFailed}</p>
      )}
    </div>
  )
}
